import React from 'react';
import { Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { StyledDialog } from 'theme/index';

/**
 * Props for ArchiveJobDialog.
 * job       - the failed job the user chose to archive; the dialog is closed when null.
 * archiving - the archive call is running, so both buttons are disabled.
 * onCancel  - closes the dialog without archiving.
 * onConfirm - archives the job.
 */
interface ArchiveJobDialogProps {
  job: { jobId: number; eventType: string } | null;
  archiving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * MM-1088: asks the user to confirm before one failed DrChrono job is archived from the
 * DrChrono Failed Events page. Uses the app's standard confirm window (StyledDialog, centred
 * "Are you sure" message, No / Yes buttons), the same as the Admin "Delete Document" confirm.
 */
export default function ArchiveJobDialog({ job, archiving, onCancel, onConfirm }: Readonly<ArchiveJobDialogProps>) {
  return (
    <StyledDialog open={!!job} onClose={archiving ? undefined : onCancel} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Archive Job</DialogTitle>
      <DialogContent sx={{ flex: '0 0 auto' }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            textAlign: 'center',
            py: 1,
          }}
        >
          <Typography variant='body1' sx={{ fontSize: '18px' }} color='text.primary'>
            Are you sure you want to archive job {job?.jobId} ({job?.eventType})?
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Button type='button' variant='outlined' onClick={onCancel} disabled={archiving}>
          No
        </Button>
        <Button
          type='button'
          variant='contained'
          color='error'
          onClick={onConfirm}
          disabled={archiving}
          startIcon={archiving ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          Yes
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
